import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';

import { useAIContext } from 'src/components/Insights/Catalog/AIProvider';
import { useCatalogPageContext } from 'src/components/Insights/Catalog/CatalogPageProvider';
import { useCodebookContext } from 'src/components/Insights/Catalog/CodebookProvider';
import { TaggingBannerDetails } from 'src/components/Insights/Catalog/TaggingBannerDetails';
import catalogSelectors from 'src/redux/catalog/catalog-selectors';
import {
  updateFilterJoin,
  updateFilters,
} from 'src/redux/catalog/catalog-slice';
import { Entry, Filter, FilterJoin, FilterName } from 'src/types/insights';
import { createProvider } from 'src/util/provider';
import { useDelayedEffect } from 'src/util/useDelayed';
import { valuelessOperations } from '../utils/filters';

export const [HighlightsPageProvider, useHighlightsPageContext] =
  createProvider(() => {
    const catalogPageContext = useCatalogPageContext();
    const { activeTab, allEntries, t, setUrlParams, urlParams, catalog } =
      catalogPageContext;

    const aiContext = useAIContext();
    const { taggingProgress, clearTagRequests } = aiContext;

    const [currentPage, setCurrentPage] = useState(1);
    const [selectedHighlightIds, setSelectedHighlightIds] = useState(
      new Set<Entry['id']>()
    );
    const [banners, setBanners] = useState<BannerItem[]>([]);

    // ••• SEARCH VALUES & ACTIONS
    const { search, searchedEntries, setSearch } =
      useSearchedEntries(allEntries);

    // FE pagination - currently not true pagination, but kept as an aesthetic choice.
    // True pagination exists for catalogs with more than 10,000 entries; see sagaLoadCatalog to change that value.
    const pages = useMemo(
      () => _.chunk(searchedEntries, HighlightsPerPage),
      [searchedEntries]
    );
    const visibleHighlights = useMemo(
      () => pages[currentPage - 1],
      [pages, currentPage]
    );

    const setCurrentPageWithQuery = useCallback(
      (page: number) => {
        setCurrentPage(page);
        setUrlParams({ ...urlParams, page }, 'push');
      },
      [urlParams, setUrlParams]
    );

    // ••• FILTER VALUES & ACTIONS

    const selectedFilters = useSelector(catalogSelectors.getSelectedFilters);
    const initialFilter = useSelector(catalogSelectors.getInitialFilter);
    const selectedFilterJoin = useSelector(catalogSelectors.getFilterJoin);
    const [openFilters, setOpenFilters] = useState(false);
    const [filters, setFilters] = useState<Filter[]>([...selectedFilters]);
    const [filterJoin, setFilterJoin] =
      useState<FilterJoin>(selectedFilterJoin);
    const resetFilters = useCallback(
      () => setFilters([...selectedFilters]),
      [selectedFilters]
    );

    const applyFilters = (newFilters: Filter[], newFilterJoin: FilterJoin) => {
      const noFiltersPresent =
        newFilters.length === 1 &&
        newFilters[0].values.length === 0 &&
        !valuelessOperations.includes(newFilters[0].operation);
      setUrlParams(
        {
          ...urlParams,
          page: 1,
          filters: noFiltersPresent
            ? undefined
            : newFilters.map((f) => JSON.stringify(f)),
          filterJoin: noFiltersPresent ? undefined : newFilterJoin,
        },
        'push'
      );
      setCurrentPage(1);
      setFilters(newFilters);
      setFilterJoin(newFilterJoin);
      dispatch(updateFilterJoin(newFilterJoin));
      dispatch(updateFilters(newFilters));
    };

    // ••• Deselect VALUES & ACTIONS
    const { selectedEntry, dispatch } = catalogPageContext;
    const unassignedCoding = useSelector(catalogSelectors.getUnassignedCoding);
    const [refreshFilters, setRefreshFilters] = useState(false);
    const codeFilters = selectedFilters.filter((f) =>
      [
        FilterName.APPLIED_CODES,
        FilterName.AI_SUGGESTED_CODES,
        FilterName.SPEAKER_DEMOGRAPHICS,
      ].includes(f.filter_name)
    );

    useEffect(() => {
      // TODO: Rework after pagination is completed
      if (unassignedCoding) {
        // allows us to look at whatever code was unassigned
        // and check against filters. This approach will send
        // a refresh even if the user re-assigns the code in
        // the same edit session (ie. without closing the highlight modal)
        let refresh = false;
        codeFilters.forEach((f) => {
          if (f.values.includes(unassignedCoding)) {
            refresh = true;
          }
        });
        setRefreshFilters(refresh);
      }
    }, [codeFilters, unassignedCoding]);

    // ••• BANNER ACTIONS

    const showBanner = (
      props: Partial<BannerItem> & Pick<BannerItem, 'message'>
    ) => {
      const banner = {
        ...props,
        id: props.id ?? `${Math.random()}`,
      };
      setBanners((prev) => [banner, ...prev]);
      return banner;
    };

    /**
     * This fucntion updates the banner item that corresponds to the banner with the same ID
     *
     * Caution: Use this function discerningly, overwriting a BannerItem will result in a re-render
     * and may cause an infinite loop if the overwrite is identical to the original.
     * @param updatedProps - { id: string; message?: string; duration?: number; details?: ReactElement; progress?: number;}
     */
    const updateBanner = (
      updatedProps: Partial<BannerItem> & Pick<BannerItem, 'id'>
    ) => {
      setBanners((prev) => {
        const bannerIndex = prev.findIndex((b) => b.id === updatedProps.id);
        if (bannerIndex == -1) return prev;

        const newBanners = [...prev];
        newBanners[bannerIndex] = {
          ...newBanners[bannerIndex],
          ...updatedProps,
        };
        return newBanners;
      });
    };

    const hideBanner = (id: BannerItem['id']) => {
      clearTagRequests();
      setBanners((prev) => prev.filter((b) => b.id !== id));
    };

    // ••• TAGGING BANNER

    const taggingBanner = banners.find((b) => b.id === TaggingBannerId);
    const bannerProps = () => ({
      id: TaggingBannerId,
      message: t(
        taggingProgress.percent === 100
          ? 'insights.taggingProgressBannerDone'
          : 'insights.taggingProgressBanner',
        { count: taggingProgress.totalCount }
      ),
      details: <TaggingBannerDetails />,
      progress: taggingProgress.percent,
      ...taggingProgress,
    });

    useEffect(() => {
      const { percent, totalCount } = taggingProgress;
      if (!totalCount) return;

      if (!taggingBanner && percent < 100) {
        showBanner(bannerProps());
      } else if (
        taggingBanner?.progress !== bannerProps().progress ||
        taggingBanner?.totalCount !== bannerProps().totalCount
      ) {
        updateBanner(bannerProps());
      }
    }, [taggingBanner, t, taggingProgress]);

    useDelayedEffect(
      () => {
        const { unsuccessfulCount, percent, totalCount } = taggingProgress;
        if ((!unsuccessfulCount && percent === 100) || !totalCount)
          hideBanner(TaggingBannerId);
      },
      8000,
      [taggingProgress]
    );

    useEffect(() => {
      if (activeTab !== 0) {
        hideBanner(TaggingBannerId);
      }
    }, [activeTab]);

    // ••• Filter Hooks

    useEffect(() => {
      if (refreshFilters && !selectedEntry) {
        // Runs iff a user had entered the highlight modal view, made a change that
        // would require a new filter call, then closed the highlight modal view
        dispatch(updateFilters(selectedFilters));
        setRefreshFilters(false);
      }
    }, [selectedEntry, dispatch, selectedFilters, refreshFilters]);

    useEffect(() => {
      const urlFilters = urlParams.filters?.map((f) => JSON.parse(f));
      if (urlFilters && catalog.id !== -1) {
        // Initialize Filters from the URL
        dispatch(updateFilters(urlFilters));
      }
      if (urlParams.page && catalog.id !== -1) {
        setCurrentPage(urlParams.page);
      }
    }, [catalog]);

    useEffect(() => {
      // Always ensure that a change in the applied filters updates the UI filters
      resetFilters();
    }, [resetFilters]);

    // ••• SCROLL TO HIGHLIGHT

    const scrollToHighlight = useCallback(
      (annotationId: number) => {
        const highlightElement = document.getElementById(
          `highlight-${annotationId}`
        );
        if (highlightElement) {
          highlightElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          return;
        }

        // Search for highlight if not found above
        const targetHighlight = allEntries.find(
          (entry) => entry.annotation_id === annotationId
        );
        if (!targetHighlight) {
          return;
        }

        // Find which page contains this highlight in available entries
        const highlightIndex = allEntries.findIndex(
          (entry) => entry.annotation_id === annotationId
        );
        const targetPage = Math.floor(highlightIndex / HighlightsPerPage) + 1;

        if (targetPage !== currentPage) {
          setCurrentPageWithQuery(targetPage);

          // Wait for the page to load, then scroll
          setTimeout(() => {
            const highlightElement = document.getElementById(
              `highlight-${annotationId}`
            );
            if (highlightElement) {
              highlightElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }, 100);
        }
      },
      [allEntries, currentPage, setCurrentPageWithQuery]
    );

    // ••• CONTEXT

    return {
      ...catalogPageContext,
      ...aiContext,
      ...useCodebookContext(),

      currentPage,
      setCurrentPage: setCurrentPageWithQuery,

      visibleHighlights,
      allHighlights: searchedEntries,
      selectedHighlightIds,
      setSelectedHighlightIds,
      clearSelection: () => setSelectedHighlightIds(new Set()),

      openFilters,
      setOpenFilters,
      selectedFilterJoin,
      filterJoin,
      setFilterJoin,
      initialFilter,
      selectedFilters,
      filters,
      setFilters,
      resetFilters,
      applyFilters,
      search,
      setSearch,
      scrollToHighlight,

      banners,
      showBanner,
      updateBanner,
      hideBanner,
    };
  });

// DEFINITIONS

export const HighlightsPerPage = 20;
const TaggingBannerId = 'taggingBanner';

export type BannerItem = {
  id: string;
  message: string;
  duration?: number;
  details?: ReactElement;
  totalCount?: number;

  /** percent: 0 - 100 */
  progress?: number;
};

const useSearchedEntries = (allEntries: Entry[]) => {
  const [search, setSearch] = useState<string>('');
  let searchedEntries = allEntries;
  if (search.length) {
    searchedEntries = allEntries.filter((e) =>
      [
        e.content?.toLowerCase(),
        e.user_name?.toLowerCase(),
        e.conversation_title?.toLowerCase(),
        e.description?.toLowerCase(),
        e.annotation_id,
      ]
        .join(' ')
        .includes(search.toLowerCase())
    );
  }
  return { searchedEntries, search, setSearch };
};
